import { useCallback, useEffect, useMemo } from 'react';
import { QueryParamConfig, useQueryParams } from 'use-query-params';

export type PageParamsConfig<T extends Record<string, unknown>> = {
  [K in keyof T]: QueryParamConfig<T[K]>;
};

export function usePageParams<T extends Record<string, unknown>>(
  config: PageParamsConfig<T>
) {
  const [query, setQuery] = useQueryParams(config);

  // Ensure all parameters have values, using defaults from config
  const params = useMemo(() => {
    const result = {} as T;

    Object.keys(config).forEach((key) => {
      const paramConfig = config[key];
      const queryValue = query[key];

      // Use query value if it exists, otherwise use config default
      if (queryValue !== null && queryValue !== undefined) {
        (result as Record<string, unknown>)[key] = queryValue;
      } else if (
        'default' in paramConfig &&
        paramConfig.default !== undefined
      ) {
        (result as Record<string, unknown>)[key] = paramConfig.default;
      } else {
        // For parameters without explicit defaults, derive from config
        (result as Record<string, unknown>)[key] =
          paramConfig.decode('') ?? null;
      }
    });

    return result;
  }, [query, config]);

  // Initialize missing parameters in URL for complete permalinking
  useEffect(() => {
    const updates = {} as Partial<typeof query>;
    let hasUpdates = false;

    Object.keys(config).forEach((key) => {
      if (query[key] === null || query[key] === undefined) {
        (updates as Record<string, unknown>)[key] = params[key as keyof T];
        hasUpdates = true;
      }
    });

    if (hasUpdates) {
      setQuery(updates, 'replaceIn');
    }
  }, [config, query, params, setQuery]);

  // Update parameters function
  const updateParams = useCallback(
    (updates: Partial<T>) => {
      const queryUpdates = {} as Partial<typeof query>;
      Object.keys(updates).forEach((key) => {
        (queryUpdates as Record<string, unknown>)[key] =
          updates[key as keyof T];
      });
      setQuery(queryUpdates, 'replaceIn');
    },
    [setQuery]
  );

  // Reset all parameters
  const resetParams = useCallback(() => {
    const resetUpdates = {} as Partial<typeof query>;
    Object.keys(config).forEach((key) => {
      (resetUpdates as Record<string, unknown>)[key] = null;
    });
    setQuery(resetUpdates, 'replaceIn');
  }, [setQuery, config]);

  return { params, updateParams, resetParams };
}
